import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';

import { Picture } from '@/components/wpl/image/src/image';
import { wacToken } from '@/libraries/design/foundations';
import { ArrowRightNoCircle } from '@/libraries/wpl/foundations/src/icons/basic/arrow-right-no-circle';

export type featureDataProps = {
	readonly headingText: string;
	readonly bodyText: string;
	readonly linkText: string;
	readonly linkUrl: string;
	readonly imageLink: string;
	readonly imageAltText?: string;
	readonly analyticsEventLabel: string;
	readonly imageHeight: number;
};

export type featureProps = {
	readonly featureData: featureDataProps;
	readonly imagePosition: 'left' | 'right';
};

type staggeredFeatureProps = {
	readonly mainHeadingText: string;
	readonly subHeadingText: string;
	readonly featureData: featureDataProps[];
	readonly startingImagePosition: 'left' | 'right';
};

const containerStyle = css({
	marginBlock: '96px',
	marginInline: '0px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexDirection: 'column',

	[media.below.lg]: {
		marginInline: '10%',
		marginBlock: '36px',
	},
});

const mainHeadingTextStyle = css({
	textAlign: 'center',
	fontFamily: wacToken('font.charlie.display'),
	fontSize: '48px',
	fontStyle: 'normal',
	fontWeight: '700',
	wordWrap: 'break-word',
	[media.only.md]: {
		fontSize: '32px',
	},
	[media.only.sm]: {
		fontSize: '32px',
	},
	[media.below.sm]: {
		fontSize: '30px',
	},
});

const subHeadingTextStyle = css({
	textAlign: 'center',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '20px',
	fontStyle: 'normal',
	fontWeight: '400',
	lineHeight: '28px',
	marginBlockStart: '16px',
	width: '768px',

	[media.below.lg]: {
		flexDirection: 'column',
		alignItems: 'center',
		marginBlockStart: '24px',
		paddingInline: '10%',
		width: '100%',
		minWidth: '316px',
	},
});

const featureContainerStyle = css({
	display: 'flex',
	flexDirection: 'row',
	marginBlockStart: '64px',
	[media.below.lg]: {
		flexDirection: 'column',
		alignItems: 'center',
		marginBlockStart: '24px',
		width: '100%',
	},
});

const featureImageContainerStyle = css({
	[media.below.lg]: {
		order: 1,
		marginBlockStart: '24px',
		maxWidth: '600px',
	},
	// disable needed to apply styling to <Picture /> component
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	img: {
		height: 'fit-content',
		maxWidth: '600px',
		[media.below.lg]: {
			maxWidth: '100%',
		},
	},
});

const featureBodyContainerStyle = css({
	display: 'flex',
	flexDirection: 'column',
	flexShrink: '0px',
	alignItems: 'center',
	marginBlockStart: '91px',
	width: '569px',
	gap: '8px',

	[media.below.lg]: {
		order: 2,
		marginBlockStart: '24px',
		width: '100%',
	},
});

const featureLinkStyles = css({
	color: wacToken('color.primary.blue'),
});

const featureHeadingTextStyle = css({
	fontSize: '32px',
	fontFamily: wacToken('font.charlie.display'),
	fontWeight: '700',
	lineHeight: '38px',
	wordWrap: 'break-word',
	marginInline: '0px',
	marginBlock: '0px',
	textAlign: 'center',
});

const featureBodyTextStyle = css({
	fontSize: '16px',
	fontFamily: wacToken('font.charlie.text'),
	fontWeight: '400',
	lineHeight: '23px',
	wordWrap: 'break-word',
	textAlign: 'center',
	maxWidth: '365px',
	padding: '0 25px',
	[media.below.lg]: {
		maxWidth: '100%',
	},
});

const Feature = ({ featureData, imagePosition }: featureProps) => {
	if (imagePosition === 'left') {
		return (
			<div css={featureContainerStyle}>
				<FeatureImage {...featureData} />
				<FeatureBody {...featureData} />
			</div>
		);
	} else {
		return (
			<div css={featureContainerStyle}>
				<FeatureBody {...featureData} />
				<FeatureImage {...featureData} />
			</div>
		);
	}
};

const FeatureImage = ({ imageLink, imageHeight }: featureDataProps) => {
	return (
		<div css={featureImageContainerStyle}>
			<Picture
				image={{
					src: imageLink,
					alt: '',
					width: 600,
					height: imageHeight,
				}}
			/>
		</div>
	);
};

const FeatureBody = ({
	headingText,
	bodyText,
	linkUrl,
	linkText,
	analyticsEventLabel,
}: featureDataProps) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	const handleClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			attributes: {
				name: `${analyticsEventLabel}`,
				section: 'staggered-feature-section',
			},
		}).fire();
	};

	return (
		<div css={featureBodyContainerStyle}>
			<h3 css={featureHeadingTextStyle}>{headingText}</h3>
			<div css={featureBodyTextStyle}>{bodyText}</div>
			<a
				href={linkUrl}
				onClick={handleClick}
				css={featureLinkStyles}
				aria-label={formatMessage(
					{
						id: 'learnMoreAbout',
						defaultMessage: 'Learn more about {subject}',
					},
					{ subject: headingText },
				)}
			>
				{linkText} <ArrowRightNoCircle />
			</a>
		</div>
	);
};

export const StaggeredFeatureList = ({
	mainHeadingText,
	subHeadingText,
	featureData,
	startingImagePosition,
}: staggeredFeatureProps) => {
	return (
		<div css={containerStyle}>
			<h2 css={mainHeadingTextStyle}>{mainHeadingText}</h2>
			<div css={subHeadingTextStyle}>{subHeadingText}</div>
			{featureData.map((feature, index) =>
				index % 2 === 0 ? (
					<Feature
						key={index}
						imagePosition={startingImagePosition === 'left' ? 'left' : 'right'}
						featureData={feature}
					/>
				) : (
					<Feature
						key={index}
						imagePosition={startingImagePosition === 'left' ? 'right' : 'left'}
						featureData={feature}
					/>
				),
			)}
		</div>
	);
};
